import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search,
  Settings,
  Webhook,
  X,
  Loader2,
  Mail,
  Phone,
  Building2,
  Calendar,
  Trash2,
  Edit2,
  Copy,
  CheckCircle2,
  Circle,
  Clock,
  MoreVertical,
  Users,
  RefreshCw,
  CheckSquare,
  Undo2,
  ChevronUp,
  ChevronDown,
  FileText,
  ShoppingBag,
} from 'lucide-react';
import { useLeads2Portal } from '../../contexts/Leads2PortalContext';
import { useToast } from '../../hooks/use-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return '';
  }
};
const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

const AssigneeBadge = ({ name, size = 'sm' }) => {
  if (!name) return null;
  const cls = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]';
  return (
    <span className="inline-flex items-center gap-1" title={`Gestito da ${name}`} data-testid="assignee-badge">
      <span className={`${cls} rounded-full bg-fuchsia-600 text-white inline-flex items-center justify-center font-semibold flex-shrink-0`}>
        {initials(name)}
      </span>
      <span className="text-[11px] text-neutral-600 truncate max-w-[90px]">{name}</span>
    </span>
  );
};

// ====================================================================
// Lead Card (used in Kanban + List)
// ====================================================================
const LeadCard = ({ lead, onClick, draggable = false, selectable = false, selected = false }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `lead-${lead.id}`,
    data: { leadId: lead.id, stageId: lead.stage_id },
    disabled: !draggable || selectable,
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.4 : 1,
  } : { opacity: isDragging ? 0.4 : 1 };

  const canDrag = draggable && !selectable;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(canDrag ? listeners : {})}
      {...(canDrag ? attributes : {})}
      onClick={(e) => {
        // Avoid triggering onClick during drag
        if (e.defaultPrevented) return;
        onClick && onClick(lead);
      }}
      data-testid={`lead-card-${lead.id}`}
      className={`bg-white rounded-xl border p-3 mb-2 cursor-pointer hover:shadow-md transition group ${selected ? 'border-fuchsia-500 ring-2 ring-fuchsia-200' : 'border-neutral-200'}`}
    >
      <div className="flex items-start justify-between gap-2">
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            readOnly
            data-testid={`lead-select-${lead.id}`}
            className="mt-0.5 w-4 h-4 accent-fuchsia-600 flex-shrink-0"
          />
        )}
        <div className="font-semibold text-sm text-neutral-800 truncate flex-1">
          {lead.full_name || lead.email || lead.phone || '— Senza nome —'}
        </div>
      </div>
      {lead.company && (
        <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1">
          <Building2 className="w-3 h-3" />
          {lead.company}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-500">
        {lead.email && (
          <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
            <Mail className="w-3 h-3" />
            {lead.email}
          </span>
        )}
        {lead.phone && (
          <span className="inline-flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {lead.phone}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        {lead.source_label || lead.source ? (
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">
            {lead.source_label || lead.source}
          </span>
        ) : <span />}
        <span className="text-[10px] text-neutral-400">{fmtDate(lead.created_at)}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-100 flex items-center justify-between gap-2">
        {lead.assigned_to_name ? (
          <AssigneeBadge name={lead.assigned_to_name} />
        ) : (
          <span className="text-[10px] text-neutral-400 italic">Non assegnato</span>
        )}
        <div className="flex items-center gap-1 flex-shrink-0">
          {lead.linked_quotes_count > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title={`${lead.linked_quotes_count} preventivi collegati`} data-testid={`card-quotes-badge-${lead.id}`}>
              <FileText className="w-3 h-3" /> {lead.linked_quotes_count}
            </span>
          )}
          {lead.linked_orders_count > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={`${lead.linked_orders_count} ordini collegati`} data-testid={`card-orders-badge-${lead.id}`}>
              <ShoppingBag className="w-3 h-3" /> {lead.linked_orders_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// Kanban Column (droppable)
// ====================================================================
const KanbanColumn = ({ stage, leads, onCardClick, selectable = false, selectedIds }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `stage-${stage.id}`, data: { stageId: stage.id } });
  const total = leads.length;
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 bg-neutral-50 rounded-xl p-3 border-2 transition ${isOver ? 'border-fuchsia-400 bg-fuchsia-50/40' : 'border-transparent'}`}
      data-testid={`kanban-column-${stage.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: stage.color || '#a21caf' }}
          />
          <h3 className="text-sm font-semibold text-neutral-800">{stage.label}</h3>
        </div>
        <span className="text-xs text-neutral-500 bg-white px-2 py-0.5 rounded-full border border-neutral-200">
          {total}
        </span>
      </div>
      <div className="min-h-[200px]">
        {leads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            onClick={onCardClick}
            draggable
            selectable={selectable}
            selected={selectable && selectedIds?.has(l.id)}
          />
        ))}
        {leads.length === 0 && (
          <div className="text-xs text-neutral-400 text-center py-6 italic">
            Nessun lead
          </div>
        )}
      </div>
    </div>
  );
};

// ====================================================================
// Create / Edit Lead Modal
// ====================================================================
const LeadFormModal = ({ open, initial, stages, assignees = [], isAdmin = true, onClose, onSave }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm(initial || { source: 'manual' });
  }, [initial, open]);

  if (!open) return null;
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="lead-form-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-semibold text-lg">{initial?.id ? 'Modifica lead' : 'Nuovo lead'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          {[
            { k: 'full_name', label: 'Nome e cognome' },
            { k: 'email', label: 'Email', type: 'email' },
            { k: 'phone', label: 'Telefono' },
            { k: 'company', label: 'Azienda' },
            { k: 'source', label: 'Sorgente' },
          ].map((f) => (
            <div key={f.k}>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{f.label}</label>
              <input
                data-testid={`lead-form-${f.k}`}
                type={f.type || 'text'}
                value={form[f.k] || ''}
                onChange={(e) => setF(f.k, e.target.value)}
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Stage</label>
            <select
              value={form.stage_id || ''}
              onChange={(e) => setF('stage_id', e.target.value)}
              data-testid="lead-form-stage"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— seleziona —</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className={isAdmin ? '' : 'hidden'}>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Gestito da (commerciale)</label>
            <select
              value={form.assigned_to || ''}
              onChange={(e) => setF('assigned_to', e.target.value)}
              data-testid="lead-form-assignee"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— Non assegnato —</option>
              {assignees.map((a) => (
                <option key={a.id} value={a.id}>{a.name}{a.role !== 'admin' ? ` (${a.code || 'agente'})` : ' (admin)'}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Note</label>
            <textarea
              data-testid="lead-form-notes"
              value={form.notes || ''}
              onChange={(e) => setF('notes', e.target.value)}
              rows={3}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-lg hover:bg-neutral-50">
            Annulla
          </button>
          <button
            data-testid="lead-form-save"
            onClick={() => onSave(form)}
            className="px-4 py-2 text-sm bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700"
          >
            Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// Main AdminLeads2
// ====================================================================
const AdminLeads2 = ({ openDetailFor = null, onNavigateDetail }) => {
  const { token: adminToken, role, Layout } = useLeads2Portal();
  const { toast } = useToast();
  const isAdmin = role === 'admin';

  const [view, setView] = useState('kanban'); // 'kanban' | 'list'
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showWebhooks, setShowWebhooks] = useState(false);
  const [showSyncPanel, setShowSyncPanel] = useState(false);
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${adminToken}`,
    'Content-Type': 'application/json',
  }), [adminToken]);

  const loadStages = async () => {
    const res = await fetch(`${API}/api/admin/leads2/stages`, { headers: authHeaders() });
    if (res.ok) {
      const j = await res.json();
      setStages(j.stages || []);
    }
  };

  const loadAssignees = async () => {
    const res = await fetch(`${API}/api/admin/leads2/assignees`, { headers: authHeaders() });
    if (res.ok) {
      const j = await res.json();
      setAssignees(j.assignees || []);
    }
  };

  const syncMattia = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/admin/leads2/mattia/sync`, { method: 'POST', headers: authHeaders() });
      const j = await res.json().catch(() => ({}));
      if (res.ok) {
        toast({ title: 'Sincronizzazione completata', description: `${j.created || 0} nuovi, ${j.updated || 0} aggiornati (su ${j.fetched || 0} dalla vista)` });
        loadStages();
        loadLeads();
      } else {
        toast({ title: 'Errore sincronizzazione', description: j.detail || 'Riprova più tardi', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Sincronizzazione non riuscita', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      const res = await fetch(`${API}/api/admin/leads2?${params.toString()}`, { headers: authHeaders() });
      if (res.ok) {
        const j = await res.json();
        setLeads(j.leads || []);
      }
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare i lead', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminToken) { loadStages(); loadLeads(); loadAssignees(); } }, [adminToken]); // eslint-disable-line
  useEffect(() => {
    if (!adminToken) return;
    const t = setTimeout(loadLeads, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const filteredLeads = useMemo(() => {
    if (!assigneeFilter) return leads;
    if (assigneeFilter === '__none__') return leads.filter((l) => !l.assigned_to);
    return leads.filter((l) => l.assigned_to === assigneeFilter);
  }, [leads, assigneeFilter]);

  const leadsByStage = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s.id] = []; });
    filteredLeads.forEach((l) => {
      if (map[l.stage_id]) map[l.stage_id].push(l);
      else {
        if (!map._unknown) map._unknown = [];
        map._unknown.push(l);
      }
    });
    return map;
  }, [filteredLeads, stages]);

  const handleDragEnd = async (event) => {
    setDraggingLeadId(null);
    const { active, over } = event;
    if (!over) return;
    const leadId = active.data?.current?.leadId;
    const fromStage = active.data?.current?.stageId;
    const toStage = over.data?.current?.stageId;
    if (!leadId || !toStage || fromStage === toStage) return;
    // Optimistic
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, stage_id: toStage } : l)));
    try {
      const res = await fetch(`${API}/api/admin/leads2/${leadId}/stage`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ stage_id: toStage }),
      });
      if (!res.ok) throw new Error();
      toast({ title: 'Spostato', description: 'Lead aggiornato' });
    } catch {
      toast({ title: 'Errore', description: 'Spostamento fallito', variant: 'destructive' });
      loadLeads();
    }
  };

  const saveLead = async (data) => {
    const method = data.id ? 'PUT' : 'POST';
    const url = data.id ? `${API}/api/admin/leads2/${data.id}` : `${API}/api/admin/leads2`;
    const payload = { ...data };
    delete payload.id;
    // Snapshot nome gestore
    if ('assigned_to' in payload) {
      const a = assignees.find((x) => x.id === payload.assigned_to);
      payload.assigned_to_name = a ? a.name : null;
    }
    try {
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      setShowForm(false);
      setEditingLead(null);
      loadLeads();
      toast({ title: 'Salvato', description: 'Lead salvato' });
    } catch {
      toast({ title: 'Errore', description: 'Salvataggio fallito', variant: 'destructive' });
    }
  };

  const openLeadDetail = (lead) => {
    if (selectMode) { toggleSelect(lead.id); return; }
    if (onNavigateDetail) onNavigateDetail(lead.id);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} lead selezionati? L'operazione è irreversibile.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/leads2/bulk-delete`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ids }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || '');
      toast({ title: 'Eliminati', description: `${j.deleted || ids.length} lead eliminati` });
      exitSelectMode();
      loadLeads();
    } catch (e) {
      toast({ title: 'Errore', description: e.message || 'Eliminazione non riuscita', variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const draggingLead = useMemo(() => leads.find((l) => l.id === draggingLeadId), [draggingLeadId, leads]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="admin-leads2-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-indigo-500 via-fuchsia-500 to-orange-500 text-white shadow">
              <Users className="w-6 h-6"/>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">LEAD 2.0</h1>
              <p className="text-sm text-neutral-500">Pipeline commerciale con lista, kanban e task</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex bg-neutral-100 rounded-xl p-1" data-testid="view-switch">
              <button
                data-testid="view-kanban-btn"
                onClick={() => setView('kanban')}
                className={`px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1.5 ${view === 'kanban' ? 'bg-white shadow text-neutral-800' : 'text-neutral-500'}`}
              >
                <LayoutGrid className="w-4 h-4"/> Kanban
              </button>
              <button
                data-testid="view-list-btn"
                onClick={() => setView('list')}
                className={`px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1.5 ${view === 'list' ? 'bg-white shadow text-neutral-800' : 'text-neutral-500'}`}
              >
                <ListIcon className="w-4 h-4"/> Lista
              </button>
            </div>
            <button
              data-testid="mattia-sync-btn"
              onClick={syncMattia}
              disabled={syncing}
              className={`${isAdmin ? 'inline-flex' : 'hidden'} items-center gap-1.5 px-3 py-2 rounded-xl border border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 text-sm disabled:opacity-50`}
              title="Sincronizza i lead dalla pipeline Mattia (Supabase)"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}/> {syncing ? 'Sincronizzo…' : 'Sincronizza ora'}
            </button>
            <button
              data-testid="open-webhooks-btn"
              onClick={() => setShowWebhooks(true)}
              className={`${isAdmin ? 'inline-flex' : 'hidden'} items-center gap-1.5 px-3 py-2 rounded-xl border border-neutral-200 hover:bg-neutral-50 text-sm`}
            >
              <Webhook className="w-4 h-4"/> Webhook
            </button>
            <button
              data-testid="open-settings-btn"
              onClick={() => setShowSettings(true)}
              className={`${isAdmin ? 'inline-flex' : 'hidden'} items-center gap-1.5 px-3 py-2 rounded-xl border border-neutral-200 hover:bg-neutral-50 text-sm`}
            >
              <Settings className="w-4 h-4"/> Pipeline
            </button>
            <button
              data-testid="open-sync-panel-btn"
              onClick={() => setShowSyncPanel(true)}
              className={`${isAdmin ? 'inline-flex' : 'hidden'} items-center gap-1.5 px-3 py-2 rounded-xl border border-neutral-200 hover:bg-neutral-50 text-sm`}
              title="Pannello sincronizzazione Mattia (annulla ultima sync)"
            >
              <Undo2 className="w-4 h-4"/> Sync
            </button>
            <button
              data-testid="toggle-select-mode-btn"
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              className={`${isAdmin ? 'inline-flex' : 'hidden'} items-center gap-1.5 px-3 py-2 rounded-xl border text-sm ${selectMode ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700' : 'border-neutral-200 hover:bg-neutral-50'}`}
              title="Seleziona più lead per eliminarli in massa"
            >
              <CheckSquare className="w-4 h-4"/> {selectMode ? 'Esci selezione' : 'Seleziona'}
            </button>
            <button
              data-testid="new-lead-btn"
              onClick={() => { setEditingLead(null); setShowForm(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-700 text-sm shadow-sm"
            >
              <Plus className="w-4 h-4"/> Nuovo lead
            </button>
          </div>
        </div>

        {/* Search + Assignee filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
            <input
              data-testid="leads-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email, telefono, azienda…"
              className="w-full border border-neutral-200 rounded-xl pl-9 pr-3 py-2 text-sm"
            />
          </div>
          <select
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className="border border-neutral-200 rounded-xl px-3 py-2 text-sm bg-white"
            data-testid="leads-assignee-filter"
          >
            <option value="">Tutti i gestori</option>
            <option value="__none__">Non assegnati</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.role !== 'admin' ? ` (${a.code || 'agente'})` : ' (admin)'}</option>
            ))}
          </select>
        </div>

        {/* Selection action bar */}
        {selectMode && (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-fuchsia-50 border border-fuchsia-200 rounded-xl px-4 py-3" data-testid="bulk-action-bar">
            <span className="text-sm text-fuchsia-800 font-medium">
              {selectedIds.size} selezionati
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={exitSelectMode}
                className="px-3 py-1.5 text-sm rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50"
                data-testid="bulk-cancel-btn"
              >
                Annulla
              </button>
              <button
                onClick={bulkDelete}
                disabled={selectedIds.size === 0 || deleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
                Elimina selezionati
              </button>
            </div>
          </div>
        )}

        {/* View */}
        {view === 'kanban' ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e) => setDraggingLeadId(e.active?.data?.current?.leadId)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggingLeadId(null)}
          >
            <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
              {stages.map((s) => (
                <KanbanColumn
                  key={s.id}
                  stage={s}
                  leads={leadsByStage[s.id] || []}
                  onCardClick={openLeadDetail}
                  selectable={selectMode}
                  selectedIds={selectedIds}
                />
              ))}
            </div>
            <DragOverlay>
              {draggingLead ? <LeadCard lead={draggingLead} /> : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden" data-testid="leads-list">
            {loading ? (
              <div className="p-10 text-center text-neutral-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto"/>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-xs uppercase text-neutral-500">
                    <tr>
                      {selectMode && (
                        <th className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            data-testid="list-select-all"
                            className="w-4 h-4 accent-fuchsia-600"
                            checked={filteredLeads.length > 0 && filteredLeads.every((l) => selectedIds.has(l.id))}
                            onChange={(e) => {
                              const all = e.target.checked;
                              setSelectedIds(() => (all ? new Set(filteredLeads.map((l) => l.id)) : new Set()));
                            }}
                          />
                        </th>
                      )}
                      <th className="text-left px-4 py-3">Nome</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Telefono</th>
                      <th className="text-left px-4 py-3">Azienda</th>
                      <th className="text-left px-4 py-3">Stage</th>
                      <th className="text-left px-4 py-3">Gestito da</th>
                      <th className="text-left px-4 py-3">Sorgente</th>
                      <th className="text-left px-4 py-3">Creato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.length === 0 && (
                      <tr><td colSpan={selectMode ? 9 : 8} className="text-center text-neutral-400 py-8">Nessun lead</td></tr>
                    )}
                    {filteredLeads.map((l) => {
                      const stage = stages.find((s) => s.id === l.stage_id);
                      return (
                        <tr
                          key={l.id}
                          onClick={() => openLeadDetail(l)}
                          data-testid={`lead-row-${l.id}`}
                          className={`border-t border-neutral-100 hover:bg-neutral-50/60 cursor-pointer ${selectMode && selectedIds.has(l.id) ? 'bg-fuchsia-50/60' : ''}`}
                        >
                          {selectMode && (
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                readOnly
                                checked={selectedIds.has(l.id)}
                                data-testid={`lead-row-select-${l.id}`}
                                className="w-4 h-4 accent-fuchsia-600"
                              />
                            </td>
                          )}
                          <td className="px-4 py-3 font-medium text-neutral-800">{l.full_name || '—'}</td>
                          <td className="px-4 py-3 text-neutral-600">{l.email || '—'}</td>
                          <td className="px-4 py-3 text-neutral-600">{l.phone || '—'}</td>
                          <td className="px-4 py-3 text-neutral-600">{l.company || '—'}</td>
                          <td className="px-4 py-3">
                            {stage && (
                              <span
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border"
                                style={{
                                  color: stage.color,
                                  borderColor: stage.color + '55',
                                  backgroundColor: stage.color + '15',
                                }}
                              >
                                {stage.label}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {l.assigned_to_name ? <AssigneeBadge name={l.assigned_to_name} /> : <span className="text-xs text-neutral-400 italic">—</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-500">{l.source_label || l.source || '—'}</td>
                          <td className="px-4 py-3 text-xs text-neutral-500">{fmtDate(l.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <LeadFormModal
        open={showForm}
        initial={editingLead}
        stages={stages}
        assignees={assignees}
        isAdmin={isAdmin}
        onClose={() => { setShowForm(false); setEditingLead(null); }}
        onSave={saveLead}
      />

      {showSettings && (
        <PipelineSettingsModal
          stages={stages}
          onClose={() => setShowSettings(false)}
          onChanged={loadStages}
          authHeaders={authHeaders}
          toast={toast}
        />
      )}

      {showWebhooks && (
        <WebhooksModal
          stages={stages}
          onClose={() => setShowWebhooks(false)}
          authHeaders={authHeaders}
          toast={toast}
        />
      )}

      {showSyncPanel && (
        <MattiaSyncModal
          onClose={() => setShowSyncPanel(false)}
          onChanged={() => { loadStages(); loadLeads(); }}
          onSync={syncMattia}
          syncing={syncing}
          authHeaders={authHeaders}
          toast={toast}
        />
      )}
    </Layout>
  );
};

// ====================================================================
// Mattia Sync Panel Modal (sync status + undo last sync)
// ====================================================================
const MattiaSyncModal = ({ onClose, onChanged, onSync, syncing, authHeaders, toast }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [undoing, setUndoing] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/leads2/mattia/status`, { headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSync = async () => {
    await onSync();
    loadStatus();
  };

  const handleUndo = async () => {
    const u = status?.undoable;
    if (!u) return;
    if (!window.confirm(`Annullare l'ultima sincronizzazione? Verranno eliminati ${u.created || 0} lead creati e ripristinati ${u.updated || 0} lead aggiornati.`)) return;
    setUndoing(true);
    try {
      const res = await fetch(`${API}/api/admin/leads2/mattia/undo`, { method: 'POST', headers: authHeaders() });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || '');
      toast({ title: 'Sincronizzazione annullata', description: `${j.deleted || 0} eliminati, ${j.restored || 0} ripristinati` });
      onChanged && onChanged();
      loadStatus();
    } catch (e) {
      toast({ title: 'Errore', description: e.message || 'Annullamento non riuscito', variant: 'destructive' });
    } finally {
      setUndoing(false);
    }
  };

  const undoable = status?.undoable;
  const last = status?.last_sync;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="mattia-sync-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-semibold text-lg flex items-center gap-2"><RefreshCw className="w-5 h-5 text-fuchsia-600"/> Sincronizzazione Mattia</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded" data-testid="sync-modal-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400"/></div>
          ) : (
            <>
              <div className="rounded-xl border border-neutral-200 p-4 bg-neutral-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-neutral-500">Stato integrazione Supabase</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status?.configured ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {status?.configured ? 'Configurata' : 'Non configurata'}
                  </span>
                </div>
                {last && (
                  <div className="mt-3 text-xs text-neutral-500">
                    Ultima sync: <span className="text-neutral-700 font-medium">{last.created || 0} nuovi, {last.updated || 0} aggiornati</span>
                    {last.synced_at ? ` · ${fmtDateTime(last.synced_at)}` : ''}
                  </div>
                )}
              </div>

              <button
                onClick={handleSync}
                disabled={syncing || !status?.configured}
                data-testid="sync-modal-sync-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-fuchsia-600 text-white hover:bg-fuchsia-700 text-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}/> {syncing ? 'Sincronizzo…' : 'Sincronizza ora'}
              </button>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-2">
                  <Undo2 className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-amber-800">Annulla ultima sincronizzazione</div>
                    {undoable ? (
                      <p className="text-xs text-amber-700 mt-1">
                        Eseguita il {fmtDateTime(undoable.created_at)} · {undoable.created || 0} creati, {undoable.updated || 0} aggiornati.
                        I lead creati verranno eliminati e quelli aggiornati ripristinati allo stato precedente.
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1">Nessuna sincronizzazione annullabile al momento.</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleUndo}
                  disabled={!undoable || undoing}
                  data-testid="sync-modal-undo-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-600 text-white hover:bg-amber-700 text-sm disabled:opacity-50"
                >
                  {undoing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Undo2 className="w-4 h-4"/>} Annulla ultima sincronizzazione
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// Pipeline Settings Modal
// ====================================================================
const PipelineSettingsModal = ({ stages, onClose, onChanged, authHeaders, toast }) => {
  const [list, setList] = useState(stages);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#a21caf');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setList(stages); }, [stages]);

  const addStage = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/leads2/stages`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ label: newLabel.trim(), color: newColor, order: list.length }),
      });
      setNewLabel('');
      onChanged();
    } finally { setSaving(false); }
  };

  const updateStage = async (id, patch) => {
    await fetch(`${API}/api/admin/leads2/stages/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(patch),
    });
    onChanged();
  };

  const deleteStage = async (id) => {
    if (!window.confirm('Cancellare questo stage?')) return;
    const res = await fetch(`${API}/api/admin/leads2/stages/${id}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      toast({ title: 'Errore', description: j.detail || 'Impossibile cancellare', variant: 'destructive' });
    } else {
      onChanged();
    }
  };

  const moveStage = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    setList(next); // ottimistico
    try {
      const res = await fetch(`${API}/api/admin/leads2/stages/reorder`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ stage_ids: next.map((s) => s.id) }),
      });
      if (!res.ok) throw new Error();
      onChanged();
    } catch {
      toast({ title: 'Errore', description: 'Riordino non riuscito', variant: 'destructive' });
      setList(stages);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pipeline-settings-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <h3 className="font-semibold text-lg">Configura pipeline</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-neutral-500">Usa le frecce per riordinare le colonne della pipeline.</p>
          {list.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-2 p-2 border border-neutral-200 rounded-lg" data-testid={`stage-row-${s.id}`}>
              <div className="flex flex-col">
                <button
                  onClick={() => moveStage(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`stage-move-up-${s.id}`}
                  className="p-0.5 text-neutral-400 hover:text-fuchsia-600 disabled:opacity-30 disabled:hover:text-neutral-400"
                  title="Sposta su"
                >
                  <ChevronUp className="w-4 h-4"/>
                </button>
                <button
                  onClick={() => moveStage(idx, 1)}
                  disabled={idx === list.length - 1}
                  data-testid={`stage-move-down-${s.id}`}
                  className="p-0.5 text-neutral-400 hover:text-fuchsia-600 disabled:opacity-30 disabled:hover:text-neutral-400"
                  title="Sposta giù"
                >
                  <ChevronDown className="w-4 h-4"/>
                </button>
              </div>
              <input
                type="color"
                value={s.color || '#a21caf'}
                onChange={(e) => updateStage(s.id, { color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border border-neutral-200"
                data-testid={`stage-color-${s.id}`}
              />
              <input
                type="text"
                defaultValue={s.label}
                onBlur={(e) => { if (e.target.value !== s.label) updateStage(s.id, { label: e.target.value }); }}
                className="flex-1 border border-neutral-200 rounded px-2 py-1 text-sm"
                data-testid={`stage-label-${s.id}`}
              />
              <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!s.is_won}
                  onChange={(e) => updateStage(s.id, { is_won: e.target.checked, is_lost: false })}
                  className="accent-emerald-600"
                />
                Vinto
              </label>
              <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!s.is_lost}
                  onChange={(e) => updateStage(s.id, { is_lost: e.target.checked, is_won: false })}
                  className="accent-red-600"
                />
                Perso
              </label>
              <button
                onClick={() => deleteStage(s.id)}
                className="p-1 text-red-500 hover:bg-red-50 rounded"
                data-testid={`stage-delete-${s.id}`}
              >
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border border-neutral-200"
            />
            <input
              type="text"
              value={newLabel}
              placeholder="Nome nuovo stage"
              onChange={(e) => setNewLabel(e.target.value)}
              className="flex-1 border border-neutral-200 rounded px-2 py-1 text-sm"
              data-testid="new-stage-label"
            />
            <button
              onClick={addStage}
              disabled={saving}
              data-testid="new-stage-add-btn"
              className="px-3 py-1 text-sm bg-fuchsia-600 text-white rounded hover:bg-fuchsia-700 disabled:opacity-50"
            >
              Aggiungi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ====================================================================
// Webhooks Modal
// ====================================================================
const WebhooksModal = ({ stages, onClose, authHeaders, toast }) => {
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState('');
  const [source, setSource] = useState('');
  const [defaultStageId, setDefaultStageId] = useState('');

  const load = async () => {
    const res = await fetch(`${API}/api/admin/leads2/webhooks/tokens`, { headers: authHeaders() });
    if (res.ok) {
      const j = await res.json();
      setTokens(j.tokens || []);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!label.trim()) return;
    await fetch(`${API}/api/admin/leads2/webhooks/tokens`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ label: label.trim(), source: source.trim() || null, default_stage_id: defaultStageId || null }),
    });
    setLabel(''); setSource(''); setDefaultStageId('');
    load();
  };

  const toggleActive = async (t) => {
    await fetch(`${API}/api/admin/leads2/webhooks/tokens/${t.id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ is_active: !t.is_active }),
    });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm('Cancellare questo webhook?')) return;
    await fetch(`${API}/api/admin/leads2/webhooks/tokens/${t.id}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  const copyUrl = (t) => {
    const url = `${API}/api/public/leads2/ingest/${t.token}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Copiato', description: 'URL webhook copiato negli appunti' });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="webhooks-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-neutral-100">
          <div>
            <h3 className="font-semibold text-lg">Webhook di ingestion</h3>
            <p className="text-xs text-neutral-500">Da usare con Make.com, Zapier, Google Sheets o altre sorgenti</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-fuchsia-50 border border-fuchsia-100 rounded-xl p-4 text-sm">
            <div className="font-semibold text-fuchsia-800 mb-1">Come usare</div>
            <ol className="list-decimal pl-5 text-neutral-700 space-y-1 text-xs">
              <li>Crea un token qui sotto (es. "Make.com - FB Lead Ads")</li>
              <li>Copia l'URL completo del webhook</li>
              <li>In Make.com crea uno scenario: trigger "Facebook Lead Ads" → modulo "HTTP make a request" con l'URL e metodo POST</li>
              <li>Mappa i campi: <code>full_name</code>, <code>email</code>, <code>phone</code>, <code>company</code>, <code>notes</code> (il backend accetta anche <code>name</code>, <code>nome</code>, <code>telefono</code>, ecc.)</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              placeholder="Nome webhook (es. Make FB)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="border border-neutral-200 rounded px-2 py-2 text-sm md:col-span-2"
              data-testid="webhook-label-input"
            />
            <input
              placeholder="Sorgente (es. facebook_lead_ads)"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="border border-neutral-200 rounded px-2 py-2 text-sm"
            />
            <select
              value={defaultStageId}
              onChange={(e) => setDefaultStageId(e.target.value)}
              className="border border-neutral-200 rounded px-2 py-2 text-sm"
            >
              <option value="">Stage iniziale (default: primo)</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <button
            onClick={create}
            data-testid="webhook-create-btn"
            className="px-4 py-2 bg-fuchsia-600 text-white rounded-lg text-sm hover:bg-fuchsia-700"
          >
            Crea webhook
          </button>

          <div className="space-y-2">
            {tokens.length === 0 && <div className="text-sm text-neutral-400 text-center py-4">Nessun webhook configurato</div>}
            {tokens.map((t) => (
              <div key={t.id} className="border border-neutral-200 rounded-xl p-3" data-testid={`webhook-row-${t.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${t.is_active ? 'bg-emerald-500' : 'bg-neutral-300'}`}/>
                    <div className="font-semibold text-sm">{t.label}</div>
                    {t.source && <span className="text-xs text-neutral-500">· {t.source}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copyUrl(t)} className="p-1 hover:bg-neutral-100 rounded" title="Copia URL">
                      <Copy className="w-4 h-4"/>
                    </button>
                    <button onClick={() => toggleActive(t)} className="text-xs px-2 py-1 border border-neutral-200 rounded hover:bg-neutral-50">
                      {t.is_active ? 'Disattiva' : 'Attiva'}
                    </button>
                    <button onClick={() => remove(t)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
                <code className="block mt-2 text-xs bg-neutral-50 border border-neutral-100 p-2 rounded break-all">
                  {API}/api/public/leads2/ingest/{t.token}
                </code>
                <div className="text-[11px] text-neutral-400 mt-1">
                  Chiamate: {t.use_count || 0} · Ultimo utilizzo: {t.last_used_at ? fmtDateTime(t.last_used_at) : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads2;
